import type React from 'react';

import type { WidgetComponentMap } from '@elara/domain/models/schemas/widget-spec';

import { assert } from '@elara/core/errors';
import { LEGACY_WIDGET_NAMES, normalizeWidgetName } from '@elara/domain/models/schemas/widget-schemas';
import { createWidgetSpecMap } from '@elara/domain/models/schemas/widget-spec';

import { FlightSearchWidget } from './FlightSearchWidget.js';
import { ImageGalleryWidget } from './ImageGalleryWidget.js';
import { ImageSearchWidget } from './ImageSearchWidget.js';
import { MapWidget } from './MapWidget.js';
import { PreferenceProposalWidget } from './PreferenceProposalWidget.js';
import { RecommendationWidget } from './RecommendationWidget.js';
import { RoomSearchWidget } from './RoomSearchWidget.js';

// Re-export the platform-neutral widget spec primitives so existing web import
// paths (`#ui/chat/widget-registry.js`) stay stable.
export type {
  AnyWidgetConfig,
  Dict,
  WidgetConfig,
  WidgetInputProps,
  WidgetReactProps,
} from '@elara/domain/models/schemas/widget-spec';
export { createWidgetConfig } from '@elara/domain/models/schemas/widget-spec';

/**
 * Web widget components, one per widget type. This is the only DOM-coupled part
 * of the registry: the validation/dispatch trunk and id back-fill live in the
 * shared `@elara/domain/models/schemas/widget-spec` module.
 */
const webWidgetComponents = {
  showImagesByIdsWidget: ImageSearchWidget,
  showRoomResultsWidget: RoomSearchWidget,
  showImageGalleryWidget: ImageGalleryWidget,
  showMapWidget: MapWidget,
  showRecommendationWidget: RecommendationWidget,
  showPreferenceProposalWidget: PreferenceProposalWidget,
  showFlightResultsWidget: FlightSearchWidget,
} satisfies WidgetComponentMap<React.ReactNode>;

/**
 * Widget registry - centralized place to register all available widgets.
 * Built from the shared spec map plus the web component map above.
 */
export const WidgetRegistry = createWidgetSpecMap<React.ReactNode>(webWidgetComponents);

/** Specific widget registry type */
export type WidgetRegistry = typeof WidgetRegistry;

export function getRegisteredWidget<WT extends keyof WidgetRegistry>(widgetType: WT): WidgetRegistry[WT] {
  const widget = WidgetRegistry[widgetType];
  assert(widget, `Widget config for ${widgetType} not found`);
  return widget;
}

/** Helper function to get available widget types */
export function getAvailableWidgetTypes(): Array<keyof WidgetRegistry> {
  return Object.keys(WidgetRegistry) as Array<keyof WidgetRegistry>;
}

/** Helper function to check if a widget type exists (supports legacy names) */
export function isWidgetTypeSupported(widgetType: string): widgetType is keyof WidgetRegistry {
  return widgetType in WidgetRegistry || widgetType in LEGACY_WIDGET_NAMES;
}

/** Helper function to get widget configuration (supports legacy names for backwards compatibility) */
export function getWidgetConfig<T extends keyof WidgetRegistry>(widgetType: T | string): WidgetRegistry[T] | undefined {
  // Normalize legacy names to current names
  const normalized = normalizeWidgetName(widgetType);
  if (!normalized) return undefined;
  return WidgetRegistry[normalized as T];
}
